export default function MarcoIslandTravelTimingSection() {
  return (
    <section id="travel-timing" className="py-16 md:py-20 bg-[#F8F9FB]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <span className="inline-block text-[#B22222] text-xs font-bold uppercase tracking-[0.15em] mb-3">
            Travel Timing Intelligence
          </span>
          <h2 className="font-serif text-2xl md:text-4xl font-bold text-[#1A365D] mb-4">
            When Marco Island Residents Need Passports Most
          </h2>
          <p className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            Understanding the travel rhythms of Marco Island helps you plan ahead — or recognize when you need emergency service.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {/* Card 1 */}
          <div className="bg-white rounded-lg p-6 md:p-8 border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#1A365D] flex items-center justify-center shrink-0">
                <i className="ri-flight-takeoff-line text-[#F6C344] text-lg"></i>
              </div>
              <h3 className="font-serif text-lg font-bold text-[#1A365D]">
                RSW — Southwest Florida International
              </h3>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              RSW is Marco Island's primary commercial airport, roughly 45–50 minutes north via I-75 and Collier Boulevard. The airport offers growing seasonal international service to Toronto, Montreal, London, and Frankfurt, plus year-round Caribbean and domestic connections. Naples Airport (APF) is closer at about 30 minutes north and serves private aviation and charter flights — a popular option for affluent Marco Island residents traveling to the Bahamas and Caribbean on their own schedule.
            </p>
            <div className="bg-[#F8F9FB] rounded-md p-3 text-xs text-gray-500">
              <span className="font-semibold text-[#1A365D]">Tip:</span> Private charter flights to the Bahamas require the same passport validity as commercial airlines — six months is the safe standard for any international destination.
            </div>
          </div>

          {/* Card 2 */}
          <div className="bg-white rounded-lg p-6 md:p-8 border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#1A365D] flex items-center justify-center shrink-0">
                <i className="ri-ship-line text-[#F6C344] text-lg"></i>
              </div>
              <h3 className="font-serif text-lg font-bold text-[#1A365D]">
                Cruise Departures &amp; Bahamas Boating
              </h3>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              Marco Island residents frequently cruise from Port Tampa Bay (~2.5 hours north), Port Everglades in Fort Lauderdale (~2 hours east via Alligator Alley), or Port of Miami (~2.25 hours east) for Caribbean itineraries. But what makes Marco Island unique is the sheer volume of international boat travel. The Marco Island Marina and local yacht clubs send boats to Bimini, Grand Bahama, and the Abacos on a weekly basis during tournament season. Bimini is roughly 50 miles across the Gulf Stream — closer by water than most Florida communities are to Orlando by car.
            </p>
            <div className="bg-[#F8F9FB] rounded-md p-3 text-xs text-gray-500">
              <span className="font-semibold text-[#1A365D]">Tip:</span> Unlike airline travel, boating to the Bahamas requires a valid passport for every crew member. Tournament captains should verify all crew passports 30 days before departure.
            </div>
          </div>

          {/* Card 3 */}
          <div className="bg-white rounded-lg p-6 md:p-8 border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#1A365D] flex items-center justify-center shrink-0">
                <i className="ri-sun-line text-[#F6C344] text-lg"></i>
              </div>
              <h3 className="font-serif text-lg font-bold text-[#1A365D]">
                Snowbirds, Seasonal Migration &amp; Luxury Travel
              </h3>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              Marco Island's wealthy snowbird population follows a pattern of extended seasonal residence with frequent international travel woven throughout. November brings arrivals from Michigan, Ohio, Illinois, New York, and Ontario. December through March features Caribbean cruises, European winter escapes, and luxury resort trips to Mexico and Costa Rica. April and May see the return migration north — often with European river cruises or Canadian family visits scheduled along the way. The affluence here means trips are frequently high-value and non-refundable, making passport emergencies particularly costly.
            </p>
            <div className="bg-[#F8F9FB] rounded-md p-3 text-xs text-gray-500">
              <span className="font-semibold text-[#1A365D]">Tip:</span> Check passport expiration before booking any non-refundable luxury travel. Charter yachts, villa rentals, and private aviation deposits are typically non-refundable within 30–60 days of departure.
            </div>
          </div>

          {/* Card 4 */}
          <div className="bg-white rounded-lg p-6 md:p-8 border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#1A365D] flex items-center justify-center shrink-0">
                <i className="ri-anchor-line text-[#F6C344] text-lg"></i>
              </div>
              <h3 className="font-serif text-lg font-bold text-[#1A365D]">
                Fishing Tournaments &amp; Bahamas Sportfishing
              </h3>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              Marco Island's deep-sea fishing and sportfishing community is among the most active in Florida. Local captains and anglers participate in tournaments in Bimini, Grand Bahama, Walker's Cay, and the Abacos throughout the year, with peak tournament season running March through June and again in the fall. These trips are not leisurely vacations — they operate on weather windows, tournament registration deadlines, and crew availability that cannot be rescheduled. A passport discovered to be expired two days before a tournament departure is a true emergency with real financial consequences.
            </p>
            <div className="bg-[#F8F9FB] rounded-md p-3 text-xs text-gray-500">
              <span className="font-semibold text-[#1A365D]">Tip:</span> Tournament captains should maintain a spreadsheet of all crew passport expiration dates. The six-month validity rule applies to Bahamas entry even for fishing tournaments.
            </div>
          </div>
        </div>

        {/* Warning callout */}
        <div className="mt-10 bg-[#FFF8E7] border-l-4 border-[#F6C344] rounded-r-lg p-6 flex items-start gap-4">
          <i className="ri-alarm-warning-line text-[#F6C344] text-xl shrink-0 mt-0.5"></i>
          <div>
            <h4 className="font-semibold text-[#1A365D] mb-1">Snowbird &amp; Tournament Season Surge</h4>
            <p className="text-gray-600 text-sm leading-relaxed">
              November through April is peak season for both snowbird travel and Bahamas fishing tournaments. Passport processing times lengthen during this period nationwide. We recommend starting renewals by September for winter travel and by February for spring tournament season. Emergency service is always available for weather-window departures and last-minute tournament entries.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}